import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../Revenue/revenue.css";
import "../Withdrawals/withdrawals.css";
import { errorMessage } from "../Revenue/revenueApi";
import { Avatar, Loading, Pager } from "../Revenue/parts";
import {
  ConfirmModal,
  CopyButton,
  NotDeployed,
  fmtAgo,
  fmtTime,
  humanize,
  isNotDeployed,
  num,
  useDebounced,
  useNotice,
} from "../Withdrawals/payoutParts";
import {
  CLAWBACK_BADGE,
  CLAWBACK_TABS,
  REFUND_STATE_BADGE,
  STORES,
  debitClawback,
  listClawbacks,
  listNegative,
  listRefunds,
  writeOffClawback,
} from "./refundsApi";

const LIMIT = 30;

const TABS = [
  { key: "refunds", label: "Refunds" },
  { key: "clawbacks", label: "Flagged clawbacks" },
  { key: "negative", label: "Negative balances" },
];

function UserMini({ user, extra = null }) {
  if (!user) return <span className="text-grey">—</span>;
  return (
    <Link className="rv-user text-white text-decoration-none" to={`/dashboard/user-management/${user.id}`} onClick={(e) => e.stopPropagation()}>
      <Avatar img={user.img} name={user.name} />
      <div style={{ minWidth: 0 }}>
        <div className="rv-user-name">
          {user.name ? `@${user.name}` : `User #${user.id}`}
          {extra}
        </div>
        <div className="rv-user-sub">
          #{user.id}
          {user.nick_name ? ` · ${user.nick_name}` : ""}
        </div>
      </div>
    </Link>
  );
}

const signed = (n, digits = 2) => <span className={Number(n) < 0 ? "rv-amount-neg" : ""}>{num(n, digits)}</span>;

/// List loader shared by the three tabs: stale answers are dropped and a
/// plain 404 means the endpoint is not on the server yet.
/// [key] changes whenever the request changes (filters / page).
function useList(fetcher, key) {
  const [state, setState] = useState({ rows: null, meta: null, counts: {}, error: "", missing: false });
  const seq = useRef(0);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const load = useCallback(async () => {
    const my = ++seq.current;
    try {
      const res = await fetchRef.current();
      if (my !== seq.current) return;
      setState({ rows: res?.data || [], meta: res?.meta || null, counts: res?.counts || {}, error: "", missing: false });
    } catch (e) {
      if (my !== seq.current) return;
      setState({
        rows: [],
        meta: null,
        counts: {},
        error: isNotDeployed(e) ? "" : errorMessage(e, "Could not load the list"),
        missing: isNotDeployed(e),
      });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load, key]);
  return [state, load];
}

/// Store refunds and what they cost: the refunds themselves, the host
/// diamonds flagged for a decision, and the users left below zero.
export default function Refunds() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "refunds");

  useEffect(() => {
    setParams(tab === "refunds" ? {} : { tab }, { replace: true });
  }, [tab, setParams]);

  return (
    <div className="rv-wrap wd-page">
      <div className="mb-4">
        <h2 className="rv-title mb-1">Refunds &amp; clawbacks</h2>
        <p className="rv-sub">
          App Store / Google Play refunds take the coins back (the buyer's balance can go below zero). Diamonds a host
          got from those coins are taken back while still on hold; released diamonds are flagged here for you to
          decide.
        </p>
      </div>

      <div className="rv-tabs mb-3">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={`rv-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "refunds" && <RefundsTab />}
      {tab === "clawbacks" && <ClawbacksTab />}
      {tab === "negative" && <NegativeTab />}
    </div>
  );
}

// ─── Refunds ──────────────────────────────────────────────────────────────

function RefundsTab() {
  const [state, setStateFilter] = useState("");
  const [store, setStore] = useState("");
  const [q, setQ] = useState("");
  const term = useDebounced(q.trim(), 400);
  const filterKey = `${state}|${store}|${term}`;
  const [pager, setPager] = useState({ key: filterKey, page: 1 });
  const page = pager.key === filterKey ? pager.page : 1;
  const [seenStates, setSeenStates] = useState([]);

  const [list, load] = useList(() => listRefunds({ state, store, q: term, page, limit: LIMIT }), `${filterKey}|${page}`);

  // The state filter offers every state the server has answered with.
  useEffect(() => {
    const found = (list.rows || []).map((r) => r.state).filter(Boolean);
    if (found.length) setSeenStates((s) => Array.from(new Set([...s, ...found])).sort());
  }, [list.rows]);

  return (
    <div className="rv-panel">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="wd-filters">
          <select className="rv-select" value={state} onChange={(e) => setStateFilter(e.target.value)}>
            <option value="">All states</option>
            {Array.from(new Set([...seenStates, ...(state ? [state] : [])])).map((s) => (
              <option key={s} value={s}>
                {humanize(s)}
              </option>
            ))}
          </select>
          <select className="rv-select" value={store} onChange={(e) => setStore(e.target.value)}>
            <option value="">All stores</option>
            {STORES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <button type="button" className="rv-btn ghost rv-btn-sm" onClick={load}>
            Refresh
          </button>
        </div>
        <input
          className="rv-input wd-search"
          placeholder="Search user or transaction ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {list.missing ? (
        <NotDeployed endpoint="GET Admin-iap-refunds" />
      ) : list.rows === null ? (
        <Loading />
      ) : (
        <>
          {list.error && <div className="rv-error mb-3">{list.error}</div>}
          {!list.rows.length ? (
            <div className="rv-empty">{term || state || store ? "No refund matches." : "No refunds yet."}</div>
          ) : (
            <div className="rv-table-wrap">
              <table className="rv-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Buyer</th>
                    <th>Store</th>
                    <th>Transaction</th>
                    <th>State</th>
                    <th className="rv-num">Credited</th>
                    <th className="rv-num">Taken back</th>
                    <th>Reason</th>
                    <th>Refunded</th>
                    <th>Reversed</th>
                  </tr>
                </thead>
                <tbody>
                  {list.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="wd-id">{r.id}</td>
                      <td>
                        <UserMini
                          user={r.user}
                          extra={
                            r.repeat_refunder ? (
                              <span className="rv-badge rv-badge-red ms-2" title="Two or more refunds in 90 days">
                                Repeat refunder
                              </span>
                            ) : null
                          }
                        />
                        {r.user && <div className="rv-user-sub mt-1">Coins now: {signed(r.user.coins)}</div>}
                      </td>
                      <td>
                        <div>{STORES.find((s) => s.key === r.store)?.label || humanize(r.store)}</div>
                        <EnvBadge env={r.environment} />
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="wd-mono">{r.transaction_id || "—"}</span>
                          <CopyButton value={r.transaction_id} />
                        </div>
                      </td>
                      <td>
                        <span className={`rv-badge ${REFUND_STATE_BADGE[r.state] || "rv-badge-violet"}`}>{humanize(r.state)}</span>
                      </td>
                      <td className="rv-num">{num(r.credited_coins)}</td>
                      <td className="rv-num">
                        <b>{num(r.refunded_coins)}</b>
                      </td>
                      <td className="wd-reason">{r.reason ? humanize(r.reason) : "—"}</td>
                      <td>
                        <div>{fmtTime(r.refunded_at)}</div>
                        <div className="rv-user-sub">{fmtAgo(r.refunded_at)}</div>
                      </td>
                      <td>{r.reversed_at ? fmtTime(r.reversed_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager meta={list.meta} onPage={(p) => setPager({ key: filterKey, page: p })} />
        </>
      )}
    </div>
  );
}

function EnvBadge({ env }) {
  if (!env) return null;
  const sandbox = String(env).toLowerCase() === "sandbox";
  return <span className={`rv-badge ${sandbox ? "rv-badge-gold" : "rv-badge-grey"} mt-1`}>{sandbox ? "Sandbox" : "Production"}</span>;
}

// ─── Clawbacks ────────────────────────────────────────────────────────────

function ClawbacksTab() {
  const [state, setStateFilter] = useState("flagged");
  const [pager, setPager] = useState({ key: state, page: 1 });
  const page = pager.key === state ? pager.page : 1;
  const [notice, setNotice] = useNotice();
  const [action, setAction] = useState(null); // {kind: debit|writeoff, row}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const [list, load] = useList(() => listClawbacks({ state, page, limit: LIMIT }), `${state}|${page}`);
  const counts = list.counts || {};
  const total = ["flagged", "held_clawed", "admin_debited", "written_off", "reversed"].reduce((s, k) => s + (Number(counts[k]) || 0), 0);

  const open = (kind, row) => {
    setNote("");
    setActionError("");
    setAction({ kind, row });
  };

  const submit = async () => {
    const { kind, row } = action;
    setBusy(true);
    setActionError("");
    try {
      const res = kind === "debit" ? await debitClawback(row.id, note) : await writeOffClawback(row.id, note);
      setAction(null);
      const now = CLAWBACK_BADGE[res?.data?.state]?.text || humanize(res?.data?.state);
      setNotice(
        res?.changed === false
          ? `Nothing changed — clawback #${row.id} is already “${now}”.`
          : kind === "debit"
          ? `Clawback #${row.id}: ${num(row.amount)} diamonds debited from @${row.host?.name || row.host?.id}.`
          : `Clawback #${row.id} written off.`
      );
      load();
    } catch (e) {
      if (isNotDeployed(e)) {
        setActionError("Not deployed yet: the server does not have this action yet.");
      } else if (e?.response?.status === 409) {
        const cur = e.response.data?.current_state;
        setActionError(
          `${errorMessage(e, "This clawback can no longer be changed")}${cur ? ` (now “${CLAWBACK_BADGE[cur]?.text || humanize(cur)}”)` : ""} — the list was refreshed.`
        );
        load();
      } else {
        setActionError(errorMessage(e, "Could not update the clawback"));
      }
    } finally {
      setBusy(false);
    }
  };

  const row = action?.row;
  const hostShort = row && Number(row.host?.diamonds) < Number(row.amount);

  return (
    <div className="rv-panel">
      {notice && <div className="rv-success mb-3">{notice}</div>}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="rv-seg violet flex-wrap">
          {CLAWBACK_TABS.map((t) => (
            <button key={t.key} type="button" className={state === t.key ? "active" : ""} onClick={() => setStateFilter(t.key)}>
              {t.label} <span className="wd-count">{num(t.key === "all" ? total : Number(counts[t.key]) || 0, 0)}</span>
            </button>
          ))}
        </div>
        <button type="button" className="rv-btn ghost rv-btn-sm" onClick={load}>
          Refresh
        </button>
      </div>

      {state === "flagged" && !list.missing && (
        <div className="wd-banner mb-3">
          These diamonds came from coins that were refunded, but they were already released to the host. Nothing was
          taken automatically: <b>Debit host</b> takes them from the host's diamonds, <b>Write off</b> leaves the host
          as is and records the loss.
        </div>
      )}

      {list.missing ? (
        <NotDeployed endpoint="GET Admin-diamond-clawbacks" />
      ) : list.rows === null ? (
        <Loading />
      ) : (
        <>
          {list.error && <div className="rv-error mb-3">{list.error}</div>}
          {!list.rows.length ? (
            <div className="rv-empty">{state === "flagged" ? "Nothing flagged." : "Nothing here."}</div>
          ) : (
            <div className="rv-table-wrap">
              <table className="rv-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Host</th>
                    <th className="rv-num">Diamonds</th>
                    <th>Gift from</th>
                    <th>Source</th>
                    <th>State</th>
                    <th>Note</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="wd-id">{r.id}</td>
                      <td>
                        <UserMini user={r.host} />
                        {r.host && (
                          <div className="rv-user-sub mt-1">
                            Available {signed(r.host.diamonds)} · held {num(r.host.diamonds_pending)}
                          </div>
                        )}
                      </td>
                      <td className="rv-num">
                        <b>{num(r.amount)}</b>
                      </td>
                      <td>{r.sender ? `@${r.sender.name || r.sender.id} (#${r.sender.id})` : "—"}</td>
                      <td className="rv-user-sub">
                        {r.refund_id ? <div>Refund #{r.refund_id}</div> : null}
                        {r.purchase_transaction_id ? (
                          <div className="d-flex align-items-center gap-1">
                            <span className="wd-mono">{r.purchase_transaction_id}</span>
                            <CopyButton value={r.purchase_transaction_id} />
                          </div>
                        ) : null}
                        {r.credit_id ? <div>Credit #{r.credit_id}</div> : null}
                      </td>
                      <td>
                        <span className={`rv-badge ${CLAWBACK_BADGE[r.state]?.cls || "rv-badge-grey"}`}>
                          {CLAWBACK_BADGE[r.state]?.text || humanize(r.state)}
                        </span>
                        {r.resolved_at && (
                          <div className="rv-user-sub mt-1">
                            {fmtTime(r.resolved_at)}
                            {r.admin_id ? ` · admin #${r.admin_id}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="wd-reason">{r.note || "—"}</td>
                      <td>
                        <div>{fmtTime(r.created_at)}</div>
                        <div className="rv-user-sub">{fmtAgo(r.created_at)}</div>
                      </td>
                      <td className="wd-actions-cell text-end">
                        {r.state === "flagged" ? (
                          <>
                            <button type="button" className="rv-btn danger rv-btn-sm" onClick={() => open("debit", r)}>
                              Debit host
                            </button>
                            <button type="button" className="rv-btn ghost rv-btn-sm" onClick={() => open("writeoff", r)}>
                              Write off
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager meta={list.meta} onPage={(p) => setPager({ key: state, page: p })} />
        </>
      )}

      {action && (
        <ConfirmModal
          title={action.kind === "debit" ? `Debit host — clawback #${row.id}` : `Write off — clawback #${row.id}`}
          confirmText={action.kind === "debit" ? `Debit ${num(row.amount)} diamonds` : "Write off"}
          tone={action.kind === "debit" ? "danger" : ""}
          busy={busy}
          disabled={action.kind === "writeoff" && note.trim().length < 2}
          error={actionError}
          onClose={() => setAction(null)}
          onConfirm={submit}
        >
          {action.kind === "debit" ? (
            <>
              <p className="rv-sub mb-2">
                Takes <b className="text-white">{num(row.amount)} diamonds</b> from @{row.host?.name || row.host?.id}'s
                available diamonds (now {num(row.host?.diamonds)}). It is saved in the host's wallet history and can be
                reversed with a Wallet adjustment.
              </p>
              {hostShort && (
                <div className="wd-warn mb-2">
                  The host has fewer available diamonds than this — the balance may go below zero.
                </div>
              )}
              <label className="rv-label">Note (optional)</label>
            </>
          ) : (
            <>
              <p className="rv-sub mb-2">
                The host keeps the {num(row.amount)} diamonds; the loss is recorded as written off. Say why.
              </p>
              <label className="rv-label">Note (required)</label>
            </>
          )}
          <textarea
            className="rv-textarea"
            rows={2}
            maxLength={500}
            value={note}
            autoFocus
            onChange={(e) => setNote(e.target.value)}
            placeholder={action.kind === "debit" ? "e.g. Refund fraud — same buyer twice" : "e.g. Small amount, good host"}
          />
        </ConfirmModal>
      )}
    </div>
  );
}

// ─── Negative balances ────────────────────────────────────────────────────

function NegativeTab() {
  const [page, setPage] = useState(1);
  const [list, load] = useList(() => listNegative({ page }), String(page));

  return (
    <div className="rv-panel">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <p className="rv-sub m-0">
          Users below zero after a refund. They cannot send gifts or withdraw until the balance is back above zero
          (buying coins or exchanging diamonds repays it). Fix a balance by hand in the Wallet.
        </p>
        <button type="button" className="rv-btn ghost rv-btn-sm" onClick={load}>
          Refresh
        </button>
      </div>

      {list.missing ? (
        <NotDeployed endpoint="GET Admin-wallet-negative" />
      ) : list.rows === null ? (
        <Loading />
      ) : (
        <>
          {list.error && <div className="rv-error mb-3">{list.error}</div>}
          {!list.rows.length ? (
            <div className="rv-empty">No user is below zero.</div>
          ) : (
            <div className="rv-table-wrap">
              <table className="rv-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th className="rv-num">Coins</th>
                    <th className="rv-num">Diamonds</th>
                    <th>Last refund</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.rows.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <UserMini user={u} />
                      </td>
                      <td className="rv-num">{signed(u.coins)}</td>
                      <td className="rv-num">{signed(u.diamonds)}</td>
                      <td>
                        <div>{fmtTime(u.last_refund_at)}</div>
                        <div className="rv-user-sub">{fmtAgo(u.last_refund_at)}</div>
                      </td>
                      <td className="wd-actions-cell text-end">
                        <Link className="rv-btn violet rv-btn-sm" to={`/dashboard/wallet?type=user&id=${u.id}`}>
                          Adjust in Wallet
                        </Link>
                        <Link className="rv-btn ghost rv-btn-sm ms-2" to={`/dashboard/user-management/${u.id}`}>
                          User
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager meta={list.meta} onPage={setPage} />
        </>
      )}
    </div>
  );
}
